// 01 · Import — le dépôt manuel des quatre exports ORIKA.
//
// Formation et tickets annulés sont contenus dans « opérations à risque » : il
// n'y a pas de fichier séparé (§7.1). Dans ce build, dépôt manuel uniquement —
// l'extension ORIKA ne changera que la provenance du texte, pas ce qui suit.
//
// Quatre choses ne se devinent pas en lisant le code :
//
//   • L'ENCODAGE. ORIKA exporte en UTF-8 avec BOM, mais un fichier ré-enregistré
//     depuis Excel revient en Latin-1. Décodé de travers, « Véronique » devient
//     « VÃ©ronique » et l'appariement des opérateurs avec les réglages tombe à
//     côté sans rien dire. La logique de la V2 est reprise telle quelle : BOM →
//     UTF-8 ; sinon UTF-8 STRICT, et Latin-1 seulement si le strict refuse. Ce
//     qui est ajouté ici : l'encodage retenu est DIT, et un décodage qui a dû
//     remplacer des caractères le dit aussi — une garde muette n'est pas une
//     garde, c'est un pari.
//
//   • LE BILAN DE RÉ-IMPORT (§7.1, absent de la V2). Les identifiants du parseur
//     sont STABLES d'un export à l'autre, par deux voies : un FNV-1a du CONTENU
//     de la ligne pour les tickets, retours, règlements et opérations à risque ;
//     une clé métier pour les deux familles qui en ont une (`op_<badge>` pour un
//     opérateur, le numéro de la machine pour un monnayeur). Dans les deux cas
//     une ligne retrouvée garde son statut — mais seule la première voie fait de
//     « retrouvée » un synonyme d'« inchangée », ce pourquoi la colonne du bilan
//     dit RETROUVÉES et pas autre chose. Un ré-import n'est donc pas un
//     écrasement — mais il DÉTACHE les statuts des lignes qui ont bougé. On
//     compte avant d'écrire, et on le montre : c'est la seule façon de savoir ce
//     qu'on va perdre.
//     Un bilan ne vaut que s'il dit VRAI : ses compteurs emploient exactement
//     les tests que le reste de l'application applique à la lecture (cf. le
//     bloc « travail déjà fait » plus bas), et ses totaux se lisent sur les
//     tableaux, pas sur des ensembles d'identifiants qui fondent les doublons.
//
//   • DÉPOSÉ ET VIDE N'EST PAS ABSENT. Un export légitimement vide (aucun
//     retour ce jour-là) est un fichier fourni : le compter dans les « états
//     manquants » réclamerait indéfiniment un fichier déjà là. L'horodatage du
//     dépôt, écrit dans l'avancement de l'étape, fait foi de la présence.
//
//   • LE GRISAGE. Ce sont ces fichiers qui décident si 03, 06, 07 et 08
//     s'appliquent aujourd'hui. La règle ne peut donc être réévaluée qu'APRÈS
//     l'écriture, jamais avant — et si elle n'a pas pu l'être (hors ligne, RPC
//     refusée), l'écran le dit et propose de la relancer.

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  detectFileType,
  parseReglements,
  parseReglementsComplet,
  parseTransfertsPaiement,
  parseRapportOperateur,
  parseRetoursCSV,
  parseOperationsArisque,
  parseOperationsSummary,
  computeOperationsImportWarnings,
} from 'sotcp-core/csvParser'
import { supabase } from '@/lib/supabase'
import { useRun } from '@/data/RunProvider'
import * as api from '@/data/api'
import { etatImport, type FichierImport } from '@/domain/derive'
import { couleurEcartUnites, fmtEcartUnites } from '@/domain/ecart'
import { messageErreur } from '@/lib/errors'
import { heure, pluriel } from '@/lib/format'
import { Card, Chip, Lamp, Notice, Modale, Chantier, Kpis } from '@/ui/primitives'
import type { Couleur } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { Rapport } from '@/domain/types'
import type { PropsEtape } from './types'

type TypeFichier = FichierImport['id']

interface Config {
  nom: string
  motif: string
  dest: string
  /** Ce que le ré-import peut détacher, nommé pour l'opérateur. */
  statuts: string
}

const CONFIG: Record<TypeFichier, Config> = {
  reglements: {
    nom: 'Suivi des règlements',
    motif: 'SUIVI_DES_REGLEMENTS_*.csv',
    dest: 'Paiements non-espèces → dépouillement (05) et PAD (03).',
    statuts: 'vérifications, PAD, notes et classements',
  },
  rapport_operateur: {
    nom: 'Rapport opérateur',
    motif: 'RAPPORT_OPERATEUR_*.csv',
    dest: 'FDC, prélèvements, déclarations → dépouillement (05).',
    statuts: 'signalements caissiers et enveloppes saisies',
  },
  retours: {
    nom: 'États des retours',
    motif: 'ETATS_DES_RETOURS_*.csv',
    dest: 'Tickets retours → étape 06.',
    statuts: 'statuts de tickets retours',
  },
  operations: {
    nom: 'Opérations à risque',
    motif: 'OPERATIONS_A_RISQUE_*.csv',
    dest: 'Formation (07), annulés (08), revue des opérations (09).',
    statuts: 'statuts annulés et formation',
  },
}

const ORDRE: TypeFichier[] = ['reglements', 'rapport_operateur', 'retours', 'operations']

/** Un dépôt dont le type est l'un des quatre CSV — donc analysable ici. */
type LigneCsv = api.LigneImport & { type: TypeFichier }
const estCsv = (l: api.LigneImport): l is LigneCsv =>
  (ORDRE as string[]).includes(l.type)

const estType = (v: string | null): v is TypeFichier =>
  v !== null && (ORDRE as string[]).includes(v)

// ── Lecture du fichier ─────────────────────────────────────────────────────

type Encodage = 'UTF-8 (BOM)' | 'UTF-8' | 'Latin-1'

interface Lecture {
  texte: string
  encodage: Encodage
  /** Le décodage a produit des caractères de remplacement : fichier douteux. */
  suspect: boolean
}

/**
 * Lecture d'un CSV en détectant son encodage. Reprise mot pour mot de la V2 :
 * BOM UTF-8 → UTF-8 ; sinon décodage UTF-8 fatal, et repli Latin-1 s'il lève.
 * Le repli n'est PAS un défaut de confort : sans lui, les accents des noms
 * d'opérateurs partent en mojibake et l'appariement avec les réglages échoue.
 *
 * Ce qui est rendu en plus : l'encodage retenu, et le fait qu'il ait fallu
 * remplacer des caractères. Le chemin BOM décode en NON fatal (un BOM annonce
 * l'UTF-8, on lui fait confiance) — un fichier mal étiqueté y produit alors des
 * U+FFFD en silence. On les compte plutôt que de les ignorer.
 */
function decoder(octets: Uint8Array): Lecture {
  const bom = octets.length >= 3 && octets[0] === 0xEF && octets[1] === 0xBB && octets[2] === 0xBF
  try {
    const strict = new TextDecoder('utf-8', { fatal: !bom })
    const texte = strict.decode(bom ? octets.subarray(3) : octets)
    return { texte, encodage: bom ? 'UTF-8 (BOM)' : 'UTF-8', suspect: texte.includes('�') }
  } catch {
    // Latin-1 ne peut pas échouer : tout octet a une image. Le repli est donc
    // toujours lisible — reste à dire qu'on y est passé.
    return { texte: new TextDecoder('iso-8859-1').decode(octets), encodage: 'Latin-1', suspect: false }
  }
}

/**
 * Un fichier déposé par l'extension ORIKA arrive en octets, pas en `File` : le
 * décodage est donc commun aux deux provenances. C'est volontaire — le brut
 * déposé par la machine doit passer EXACTEMENT par le même chemin que celui
 * qu'on lâche à la main, sinon il y a deux imports au lieu d'un.
 */
function lireFichier(fichier: File): Promise<Lecture> {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader()
    lecteur.onerror = () => reject(new Error('Erreur de lecture du fichier.'))
    lecteur.onload = () => {
      const buf = lecteur.result
      if (!(buf instanceof ArrayBuffer)) { reject(new Error('Fichier illisible.')); return }
      resolve(decoder(new Uint8Array(buf)))
    }
    lecteur.readAsArrayBuffer(fichier)
  })
}

/**
 * SHA-256 hexadécimal, ou `null` si le navigateur ne l'offre pas.
 *
 * `crypto.subtle` n'existe que sur une origine sécurisée : il est là en HTTPS,
 * absent derrière l'adresse tailnet en clair. L'empreinte étant facultative en
 * base, son absence n'empêche rien — elle ne fait que priver ce dépôt-là du
 * dédoublonnage.
 */
async function empreinteSha256(fichier: File): Promise<string | null> {
  if (!globalThis.crypto?.subtle) return null
  try {
    const somme = await crypto.subtle.digest('SHA-256', await fichier.arrayBuffer())
    return [...new Uint8Array(somme)].map((o) => o.toString(16).padStart(2, '0')).join('')
  } catch {
    return null
  }
}

/** Les 300 premiers octets suffisent à la détection du type (V2). */
function premiereLigne(fichier: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader()
    lecteur.onerror = () => reject(new Error('Erreur de lecture du fichier.'))
    lecteur.onload = () => resolve(String(lecteur.result ?? '').split('\n')[0] ?? '')
    lecteur.readAsText(fichier.slice(0, 300), 'ISO-8859-1')
  })
}

// ── Le bilan de ré-import ──────────────────────────────────────────────────

interface DiffFamille {
  nom: string
  avant: number
  apres: number
  communs: number
  ajoutees: number
  retirees: number
}

interface Bilan {
  type: TypeFichier
  fichier: string
  encodage: Encodage
  familles: DiffFamille[]
  lignes: number
  /** Lignes que le blob portait avant ce dépôt : sans elles, rien n'est « remplacé ». */
  avant: number
  /** Statuts déjà posés qui retombent sur une ligne du nouveau fichier. */
  conserves: number
  /** Statuts dont la ligne n'existe plus : ils ne servent plus à rien. */
  orphelins: number
  /** Lignes que le nouveau fichier ne contient plus, toutes familles confondues. */
  retirees: number
  /** Quelque chose EST détruit : des lignes disparaissent, ou du travail se détache. */
  perte: boolean
  /** Perte, fichier sans ligne, ou doute d'encodage : trois raisons de demander. */
  confirmation: boolean
  avertissements: string[]
  patch: Record<string, unknown>
}

interface Depot { at: string; fichier: string; lignes: number; encodage: string | null }

const tableau = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? (v as Record<string, unknown>[]) : []

const objet = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {}

const cles = (v: unknown, champ: 'id' | 'ticket'): Set<string> =>
  new Set(tableau(v).map((l) => String(l[champ] ?? '')).filter(Boolean))

/**
 * Multi-ensemble des identifiants. Un ENSEMBLE fondait les doublons : seuls les
 * paiements portent un suffixe d'occurrence (BUG-7 du 22/05/2026), tous les
 * autres identifiants sont des hachés de contenu nus — deux lignes rigoureusement
 * identiques (deux annulés au même ticket, même montant) partagent donc leur id.
 * Le bilan annonçait alors moins de lignes que l'étape qui allait les afficher.
 */
function multiCompte(v: unknown): Map<string, number> {
  const m = new Map<string, number>()
  for (const l of tableau(v)) {
    const id = String(l.id ?? '')
    if (!id) continue
    m.set(id, (m.get(id) ?? 0) + 1)
  }
  return m
}

/** Diff d'une famille par identifiant de contenu — c'est là que tout se joue. */
function comparer(nom: string, avant: unknown, apres: unknown): DiffFamille {
  const a = multiCompte(avant)
  const b = multiCompte(apres)
  let communs = 0
  for (const [id, n] of b) communs += Math.min(n, a.get(id) ?? 0)
  // Les totaux se lisent sur les TABLEAUX : une ligne sans identifiant existe
  // quand même, et elle sera affichée par son étape. La compter hors du diff
  // (donc en « ajoutée » ou « retirée ») est la lecture honnête — on ne sait pas
  // la rattacher, donc son statut ne survivra pas.
  const avantN = tableau(avant).length
  const apresN = tableau(apres).length
  return { nom, avant: avantN, apres: apresN, communs, ajoutees: apresN - communs, retirees: avantN - communs }
}

// Ce qui compte comme « travail déjà fait ». Le test employé ici est CELUI QUE
// L'APPLICATION APPLIQUE À LA LECTURE, jamais un autre — sinon le bilan sur
// lequel on décide d'écraser ne parle pas de la même chose que l'écran d'où
// vient le travail.
//
// Convention V2, tenue à la lettre : « clé absente = jamais vu », et un ticket
// VALIDÉ vaut `null` (set_ticket_status écrit null). Juger sur la valeur
// (`.status`) rendait invisibles TOUS les tickets validés : le bilan annonçait
// « 0 statut conservé » là où l'étape 08 affichait « 88 / 88 statués ». Même
// erreur sur `verifications`, que derive.ts compte par présence de la clé.

/** Présence de la clé — la convention V2 elle-même (derive.ts : hasOwnProperty). */
const TOUJOURS = (): boolean => true
/** Un PAD vérifié, c'est un `type` posé (V2 : `padVerifs?.[p.id]?.type`). */
const aPad = (v: unknown): boolean => Boolean(objet(v).type)
/** Un signalement, c'est un texte (V2 : `.filter(([, r]) => r && r.text)`). */
const aTexte = (v: unknown): boolean => Boolean(objet(v).text)
/** Une note, c'est une chaîne non vide (Dépouillement écrit la chaîne nue). */
const aNote = (v: unknown): boolean => typeof v === 'string' && v.trim() !== ''
/** Un classement de bon, une remise, des enveloppes : une valeur réellement posée. */
const aValeur = (v: unknown): boolean => {
  if (v === null || v === undefined || v === '') return false
  return typeof v === 'object' ? Object.keys(objet(v)).length > 0 : true
}

/** Combien de statuts déjà posés survivent au remplacement, combien se détachent. */
function survie(
  statuts: unknown,
  apres: Set<string>,
  porte: (v: unknown) => boolean,
): [number, number] {
  let conserves = 0
  let orphelins = 0
  for (const [cle, valeur] of Object.entries(objet(statuts))) {
    if (!porte(valeur)) continue
    if (apres.has(cle)) conserves++
    else orphelins++
  }
  return [conserves, orphelins]
}

/** Cumule la survie de plusieurs familles de travail indexées par la même clé. */
function survies(
  sources: Array<[unknown, (v: unknown) => boolean]>,
  apres: Set<string>,
): [number, number] {
  let c = 0
  let o = 0
  for (const [src, porte] of sources) {
    const [ci, oi] = survie(src, apres, porte)
    c += ci
    o += oi
  }
  return [c, o]
}

/**
 * Parse le texte et prépare TOUT — le patch exact de la V2 et le bilan — sans
 * rien écrire. Séparer l'analyse de l'écriture est ce qui permet de montrer le
 * bilan avant de décider.
 */
function analyser(type: TypeFichier, lu: Lecture, r: Rapport | null, fichier: string): Bilan {
  const familles: DiffFamille[] = []
  let patch: Record<string, unknown> = {}
  let conserves = 0
  let orphelins = 0
  const avertissements: string[] = []

  if (lu.suspect) {
    avertissements.push(
      'Des caractères n’ont pas pu être décodés (affichés « � ») — le fichier annonce '
      + 'un encodage qu’il ne respecte pas. Les noms d’opérateurs risquent de ne pas s’apparier.',
    )
  }

  if (type === 'reglements') {
    const paiements = parseReglements(lu.texte)
    patch = {
      payments:           paiements,
      reglementsComplet:  parseReglementsComplet(lu.texte),
      transfertsPaiement: parseTransfertsPaiement(lu.texte),
    }
    familles.push(comparer('Règlements', r?.payments, paiements))
    // Tout ce qui est indexé par l'id du paiement se détache avec lui : les
    // vérifications et les PAD, mais aussi les notes, le classement des bons
    // divers et les remises de non-conformes. N'en compter que deux, c'était
    // annoncer une perte plus petite que la vraie.
    const ids = cles(paiements, 'id')
    const [c, o] = survies([
      [r?.verifications,  TOUJOURS],
      [r?.padVerifs,      aPad],
      [r?.notes,          aNote],
      [r?.bonDiversTypes, aValeur],
      [r?.paymentMeta,    aValeur],
    ], ids)
    conserves = c
    orphelins = o
    // Les lignes sans badge ne sont plus fondues dans un faux opérateur 9999
    // (audit BUG-1 du 22/05/2026) : elles apparaissent à part, il faut le dire.
    const sansBadge = paiements.unbadgedCount ?? 0
    if (sansBadge > 0) {
      avertissements.push(
        `${pluriel(sansBadge, 'paiement')} sans badge opérateur — affichés séparément dans Dépouillement.`,
      )
    }
  } else if (type === 'rapport_operateur') {
    const { operators, monnayeurs } = parseRapportOperateur(lu.texte)
    patch = { rapportOps: operators, monnayeurs }
    familles.push(comparer('Opérateurs', r?.rapportOps, operators))
    familles.push(comparer('Monnayeurs', r?.monnayeurs, monnayeurs))
    // Les signalements caissiers sont indexés par badge, pas par id de ligne.
    // Un badge se lit dans le rapport opérateur OU dans le libellé d'un paiement
    // (« NOM (1234) », comme la V2 résout ses noms) — et ce dépôt-ci ne touche
    // PAS les paiements. Un signalement dont le badge ne vit que là n'est donc
    // pas détaché : ne pas l'unir ici, c'est annoncer une perte qui n'a pas lieu.
    const badges = new Set(operators.map((o) => String(o.badge ?? '')).filter(Boolean))
    for (const p of tableau(r?.payments)) {
      const b = /\((\d+)\)/.exec(String(p.operateur ?? ''))?.[1]
      if (b) badges.add(b)
    }
    const [cs, os] = survie(r?.operatorReports, badges, aTexte)
    // Les enveloppes de prélèvement saisies au dépouillement sont indexées par
    // l'id d'opérateur (`op_<badge>`, ou un haché de contenu pour une ligne sans
    // badge — celui-là bouge dès que la ligne bouge). C'est du travail, il compte.
    const [ce, oe] = survie(r?.rapportState, cles(operators, 'id'), aValeur)
    conserves = cs + ce
    orphelins = os + oe
  } else if (type === 'retours') {
    const retours = parseRetoursCSV(lu.texte)
    patch = { retours }
    familles.push(comparer('Tickets retours', r?.retours, retours))
    // Convention V2 : les statuts de tickets sont indexés par NUMÉRO de ticket,
    // et un ticket validé vaut `null` — donc présence de la clé, rien d'autre.
    const [c, o] = survie(r?.retoursStatuses, cles(retours, 'ticket'), TOUJOURS)
    conserves = c
    orphelins = o
  } else {
    const ops = parseOperationsArisque(lu.texte)
    const { formation, annules, annulationsLigne, autorisations, remises, changementsPrix } = ops
    patch = { formation, annules, annulationsLigne, autorisations, remises, changementsPrix, operationsLoaded: true }
    familles.push(comparer('Tickets annulés', r?.annules, annules))
    familles.push(comparer('Mode formation', r?.formation, formation))
    familles.push(comparer('Annulations ligne', r?.annulationsLigne, annulationsLigne))
    familles.push(comparer('Autorisations superviseur', r?.autorisations, autorisations))
    familles.push(comparer('Remises', r?.remises, remises))
    familles.push(comparer('Changements de prix', r?.changementsPrix, changementsPrix))
    const [c, o] = survie(r?.annulesStatuses, cles(annules, 'ticket'), TOUJOURS)
    const [cf, of_] = survie(r?.formationStatuses, cles(formation, 'ticket'), TOUJOURS)
    conserves = c + cf
    orphelins = o + of_
    // Canary de l'incident du 01/06/2026 : le bloc FILTRES du fichier annonce le
    // nombre de tickets annulés attendu. Sans ce garde-fou, un changement de
    // format ORIKA est invisible — la page Annulés a juste l'air vide.
    for (const a of computeOperationsImportWarnings(ops, parseOperationsSummary(lu.texte))) {
      avertissements.push(a)
    }
  }

  const lignes = familles.reduce((s, f) => s + f.apres, 0)
  const avant = familles.reduce((s, f) => s + f.avant, 0)
  const retirees = familles.reduce((s, f) => s + f.retirees, 0)
  // On ne demande confirmation que là où il y a QUELQUE CHOSE À PERDRE : des
  // lignes qui disparaissent, du travail qui se détache. Un ré-import qui ne fait
  // qu'ajouter est réversible — on ne fait pas cliquer pour rien onze fois par
  // jour. Deux doutes s'y ajoutent, qui ne se rattrapent pas après coup : un
  // fichier mal décodé, et un fichier qui ne produit aucune ligne.
  //
  // PERDRE et DEMANDER ne sont pas la même chose, et les confondre faisait
  // mentir l'écran : un export légitimement vide déposé sur un bloc encore vierge
  // n'écrase rien du tout, mais il s'annonçait en rouge « remplacerait les
  // données du jour par du vide » — des données qui n'existaient pas. Il mérite
  // un regard (déposer zéro ligne GRISE les étapes qui en dépendent), pas une
  // alarme. Un fichier vide sur un bloc rempli, lui, détruit vraiment — et il
  // tombe déjà dans `retirees`.
  const perte = retirees > 0 || orphelins > 0
  return {
    type, fichier, encodage: lu.encodage, familles, lignes, avant, conserves, orphelins, retirees,
    avertissements, patch, perte, confirmation: perte || lignes === 0 || lu.suspect,
  }
}

function lireDepot(v: unknown): Depot | null {
  const o = objet(v)
  if (typeof o.at !== 'string') return null
  return {
    at: o.at,
    fichier: typeof o.fichier === 'string' ? o.fichier : '—',
    lignes: typeof o.lignes === 'number' ? o.lignes : 0,
    encodage: typeof o.encodage === 'string' ? o.encodage : null,
  }
}

// ── Trois états, pas deux ──────────────────────────────────────────────────
//
// `etatImport` (partagé) ne connaît que « chargé » / « non chargé », et pour le
// rapport opérateur il conclut sur le NOMBRE de lignes : un export légitimement
// vide s'affichait « ABSENT » et se comptait indéfiniment dans les états
// manquants — l'écran réclamait un fichier déjà fourni. Un dépôt laisse une
// trace horodatée dans l'avancement de l'étape : elle fait foi de la présence,
// le contenu ne fait foi que du remplissage.

type EtatBloc = 'absent' | 'vide' | 'charge'

/**
 * Ce qu'une carte est en train de faire. « En attente » n'est pas « rien » : un
 * fichier accepté qui attend son tour doit le dire, sinon le geste a l'air perdu
 * et l'opérateur le refait.
 */
type Travail = 'attente' | 'lecture'

/** Le nombre de lignes que le blob porte pour chaque fichier — sa vérité du jour. */
const LIGNES: Record<TypeFichier, (r: Rapport | null) => number> = {
  reglements:        (r) => tableau(r?.payments).length,
  rapport_operateur: (r) => tableau(r?.rapportOps).length + tableau(r?.monnayeurs).length,
  retours:           (r) => tableau(r?.retours).length,
  operations:        (r) => tableau(r?.annules).length + tableau(r?.formation).length
    + tableau(r?.annulationsLigne).length + tableau(r?.autorisations).length
    + tableau(r?.remises).length + tableau(r?.changementsPrix).length,
}

const etatBloc = (charge: boolean, depot: Depot | null, lignes: number): EtatBloc =>
  !charge && !depot ? 'absent' : lignes > 0 ? 'charge' : 'vide'

const LAMPE: Record<EtatBloc, Couleur> = { absent: 'warn', vide: 'neutre', charge: 'ok' }
const MOT: Record<EtatBloc, string> = { absent: 'ABSENT', vide: 'DÉPOSÉ · VIDE', charge: 'CHARGÉ' }

// ── L'écran ────────────────────────────────────────────────────────────────

export default function Import({ etape, rapport, lectureSeule }: PropsEtape) {
  const { date, ecrireRapport, recalculerRegles, noter, enLigne } = useRun()
  const toast = useToast()

  const [depots, setDepots] = useState<Partial<Record<TypeFichier, Depot>>>({})
  const [bilans, setBilans] = useState<Partial<Record<TypeFichier, Bilan>>>({})
  const [alertes, setAlertes] = useState<Partial<Record<TypeFichier, string[]>>>({})
  const [erreurs, setErreurs] = useState<Partial<Record<TypeFichier, string>>>({})
  const [travail, setTravail] = useState<Partial<Record<TypeFichier, Travail>>>({})
  const [aConfirmer, setAConfirmer] = useState<Bilan | null>(null)
  /** Le grisage n'a pas pu être réévalué après une écriture : il est en retard. */
  const [regrisageDu, setRegrisageDu] = useState(false)
  const [regrisageEnCours, setRegrisageEnCours] = useState(false)
  // La réponse à la modale est attendue par le traitement lui-même : c'est ce
  // qui permet de déposer quatre fichiers d'un coup sans perdre la main.
  const reponse = useRef<((ok: boolean) => void) | null>(null)
  // UN dépôt à la fois, mais AUCUN dépôt perdu. `reponse` n'a qu'une place : deux
  // analyses menées de front écrasaient la promesse de la première, qui ne se
  // résolvait alors JAMAIS — sa carte restait « Lecture du fichier… » et son
  // `finally` ne partait plus. Refuser le second dépôt lève l'interblocage, mais
  // jette le fichier : à 7 h, quatre exports lâchés coup sur coup sur leurs
  // quatre cartes en perdaient trois, et il fallait s'en apercevoir au toast pour
  // les redéposer un par un. Ils font donc la queue. L'ordre de dépôt est tenu,
  // ce qui garde aussi le bilan juste : le rapport opérateur va chercher ses
  // badges dans les paiements, il doit les lire une fois écrits.
  const chaine = useRef<Promise<void>>(Promise.resolve())
  const enfiler = (tache: () => Promise<void>) => {
    // `.then(t, t)` : la file avance même si le maillon précédent a rompu.
    chaine.current = chaine.current.then(tache, tache)
  }
  // Quatre fichiers déposés d'un coup s'enchaînent plus vite que les rendus :
  // l'état React serait périmé au deuxième. La référence, elle, est à jour.
  const horodatages = useRef<Record<string, unknown>>({})
  // Même raison, mais côté lecture : le rapport arrive par les props, et la
  // boucle de dépôt groupé garde celui d'AVANT la première écriture. Or le
  // rapport opérateur va chercher les badges dans les PAIEMENTS — déposer les
  // règlements puis le rapport opérateur annoncerait une perte calculée sur les
  // anciens paiements. On tient donc une vue avancée par chaque patch appliqué,
  // resynchronisée dès que les props bougent.
  const vue = useRef<Rapport | null>(rapport)
  const propsVues = useRef<Rapport | null>(rapport)
  if (propsVues.current !== rapport) { propsVues.current = rapport; vue.current = rapport }
  // `ecrireRapport` se referme sur le rapport de SON rendu pour pouvoir annuler
  // son écriture en cas d'échec. Une tâche mise à la queue s'exécute plusieurs
  // rendus plus tard : appelée telle qu'elle a été capturée, l'annulation du 2ᵉ
  // dépôt aurait ramené l'écran à l'état d'AVANT le 1ᵉʳ — un import réussi effacé
  // à l'écran par l'échec du suivant. On appelle donc toujours la version fraîche.
  const ecrireRef = useRef(ecrireRapport)
  ecrireRef.current = ecrireRapport

  const { fichiers } = etatImport(rapport)
  const depotsBase = objet(objet(etape.avancement).imports)

  const depotDe = (id: TypeFichier): Depot | null => depots[id] ?? lireDepot(depotsBase[id])
  const etats = fichiers.map((f) => etatBloc(f.charge, depotDe(f.id), LIGNES[f.id](rapport)))
  const absents = etats.filter((e) => e === 'absent').length
  const vides = etats.filter((e) => e === 'vide').length
  const lignesJour = ORDRE.reduce((s, id) => s + LIGNES[id](rapport), 0)
  const detaches = ORDRE.reduce((s, id) => s + (bilans[id]?.orphelins ?? 0), 0)
  const depotsSession = ORDRE.filter((id) => bilans[id]).length

  /**
   * Réévaluation de la règle de grisage. `annonce` : à la main on veut le
   * retour, après un import on ne veut pas un toast de plus.
   */
  const reevaluer = async (annonce: boolean) => {
    setRegrisageEnCours(true)
    try {
      await recalculerRegles()
      setRegrisageDu(false)
      if (annonce) toast('GRISAGE RÉÉVALUÉ')
    } catch (e: unknown) {
      setRegrisageDu(true)
      toast(messageErreur(e), true)
    } finally {
      setRegrisageEnCours(false)
    }
  }

  // ── La boîte aux lettres de l'extension ORIKA ────────────────────────────
  // L'extension dépose le BRUT ; l'ingestion reste ici, dans la run ouverte,
  // avec son verrou, son bilan de ré-import et sa confirmation. C'est ce qui
  // permet qu'il n'existe qu'un seul parseur : celui qui se déploie avec le
  // serveur, pas celui qui dort dans une extension installée à la main.
  // La boîte transporte aussi les tickets de tiroir des caisses automatiques.
  // Ils n'ont rien à faire ici : ils nourrissent l'étape 02A, pas le blob. Le
  // prédicat les écarte À LA FRONTIÈRE, si bien que le reste de l'écran ne
  // manipule que des dépôts qu'il sait analyser.
  const [attente, setAttente] = useState<LigneCsv[]>([])
  const [ingestion, setIngestion] = useState<string | null>(null)

  const relireBoite = useCallback(() => {
    api.listerImports(date, 'en_attente')
      .then((l) => setAttente(l.filter(estCsv)))
      .catch(() => { /* la boîte est un confort : son absence ne casse pas l'étape */ })
  }, [date])

  useEffect(() => {
    relireBoite()
    // Le fichier doit apparaître pendant qu'on regarde l'écran : l'extension
    // dépose quand ORIKA a fini, pas quand l'opérateur pense à rafraîchir.
    const canal = supabase.channel(`fc-imports-${date}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'fc_imports', filter: `journee_date=eq.${date}` },
        relireBoite)
      .subscribe()
    return () => { void supabase.removeChannel(canal) }
  }, [date, relireBoite])

  /** Ingérer un dépôt : le brut redescend, et repart dans le chemin habituel. */
  const ingerer = async (ligne: LigneCsv) => {
    if (lectureSeule || ingestion) return
    setIngestion(ligne.id)
    try {
      const octets = await api.telechargerImport(ligne.chemin)
      const lu = decoder(octets)
      const bilan = analyser(ligne.type, lu, vue.current, ligne.fichier ?? ligne.chemin)
      if (bilan.confirmation && !(await demander(bilan))) {
        toast('INGESTION ABANDONNÉE — RIEN N’A ÉTÉ ÉCRIT')
        return
      }
      await appliquer(bilan)
      await api.majStatutImport(ligne.id, 'ingere', bilan.lignes)
      relireBoite()
    } catch (e) {
      const msg = messageErreur(e)
      toast(msg, true)
      // L'échec reste attaché au dépôt : sans cela, le fichier repasse en
      // attente et on le retente indéfiniment sans savoir pourquoi.
      await api.majStatutImport(ligne.id, 'erreur', null, msg).catch(() => {})
      relireBoite()
    } finally {
      setIngestion(null)
    }
  }

  const ecarter = async (ligne: api.LigneImport) => {
    try {
      await api.majStatutImport(ligne.id, 'ignore', null, 'Écarté à la main')
      toast('DÉPÔT ÉCARTÉ — IL RESTE ARCHIVÉ')
      relireBoite()
    } catch (e) { toast(messageErreur(e), true) }
  }

  const trancher = (ok: boolean) => {
    setAConfirmer(null)
    const r = reponse.current
    reponse.current = null
    r?.(ok)
  }

  const demander = (b: Bilan) => new Promise<boolean>((resolve) => {
    reponse.current = resolve
    setAConfirmer(b)
  })

  const appliquer = async (b: Bilan) => {
    await ecrireRef.current(`import · ${CONFIG[b.type].nom}`, b.patch,
      () => api.patcherRapport(date, b.patch), '01')

    // La vue avance avec l'écriture : le dépôt suivant de la même rafale lira
    // le blob tel qu'il est maintenant, pas tel qu'il était au premier rendu.
    vue.current = { ...(vue.current ?? {}), ...b.patch } as Rapport

    const d: Depot = {
      at: new Date().toISOString(), fichier: b.fichier, lignes: b.lignes, encodage: b.encodage,
    }
    setDepots((p) => ({ ...p, [b.type]: d }))
    setBilans((p) => ({ ...p, [b.type]: b }))
    // L'avertissement ne se pose sur la carte qu'une fois l'écriture faite : un
    // dépôt abandonné laissait sinon une alerte à l'écran pour un fichier qui
    // n'est jamais entré.
    setAlertes((p) => ({ ...p, [b.type]: b.avertissements.length > 0 ? b.avertissements : undefined }))
    // L'horodatage du dépôt n'a rien à faire dans le blob V2 : il vit dans
    // l'avancement de l'étape, qui appartient à FlashCP.
    horodatages.current = { ...depotsBase, ...horodatages.current, [b.type]: d }
    void api.toucherEtape(date, '01', { imports: horodatages.current }).catch(() => {})

    toast(`${CONFIG[b.type].nom.toUpperCase()} — ${pluriel(b.lignes, 'LIGNE').toUpperCase()}`)

    // Le contenu du fichier décide du grisage de 03 / 06 / 07 / 08 : la règle ne
    // peut être réévaluée qu'une fois l'écriture faite, et elle se journalise.
    // `enLigne` peut dater du rendu précédent — l'état du navigateur tranche.
    if (enLigne && navigator.onLine !== false) await reevaluer(false)
    else {
      setRegrisageDu(true)
      toast('HORS LIGNE — GRISAGE À RÉÉVALUER AU RETOUR', true)
    }

    const aj = b.familles.reduce((s, f) => s + f.ajoutees, 0)
    void noter(
      `Import ${CONFIG[b.type].nom} (${b.fichier}, ${b.encodage}) — ${b.lignes} ligne(s), `
      + `+${aj} / −${b.retirees}, ${b.conserves} statut(s) conservé(s)`
      + `${b.orphelins > 0 ? `, ${b.orphelins} détaché(s)` : ''}.`,
      '01',
    ).catch(() => {})
  }

  /**
   * Chemin unique de dépôt. `carte` : la carte visée par le geste, `null` pour un
   * lâcher n'importe où sur l'écran. La détection prime sur la carte — un CSV
   * lâché sur la mauvaise était parsé comme le type de la carte : zéro ligne, et
   * l'écrasement d'une famille par du vide.
   *
   * La reconnaissance se fait AVANT la file : elle ne touche à rien de partagé,
   * et c'est elle qui dit quelle carte doit s'allumer « en attente » tout de
   * suite. Rendre la main sans attendre l'écriture est ce qui permet de lâcher
   * les quatre fichiers d'affilée et de voir les quatre cartes accuser réception.
   */
  /**
   * Archive le fichier d'origine dans le bucket, au même endroit et sous la même
   * convention que les dépôts de l'extension. Best-effort : un échec de coffre
   * ne doit jamais faire échouer un import qui, lui, a réussi.
   *
   * L'empreinte n'est calculée que si `crypto.subtle` existe — il n'est offert
   * que sur une origine sécurisée. En HTTPS (flashcp.dutchki.dev) le dépôt
   * manuel se dédoublonne donc comme celui de l'extension ; sur le tailnet en
   * HTTP il s'archive sans empreinte, ce que la base accepte.
   */
  const archiver = async (fichier: File, type: TypeFichier) => {
    const chemin = api.cheminImport(date, type, fichier.name)
    await api.televerserImport(chemin, fichier)
    const ligne = await api.declarerImport({
      date, type, chemin, fichier: fichier.name, octets: fichier.size, source: 'manuel',
      empreinte: await empreinteSha256(fichier),
    })
    // Il vient d'être ingéré à l'instant : il n'a rien à faire dans la boîte.
    await api.majStatutImport(ligne.id, 'ingere', null, 'Déposé à la main dans l’étape 01')
  }

  const deposer = async (fichier: File, carte: TypeFichier | null) => {
    if (lectureSeule) return
    let detecte: TypeFichier | null = null
    try {
      const t = detectFileType(fichier.name, await premiereLigne(fichier))
      if (estType(t)) detecte = t
    } catch { /* illisible : le dépôt lui-même le dira, avec l'erreur exacte. */ }

    const cible = detecte ?? carte
    if (!cible) { toast(`TYPE NON RECONNU — ${fichier.name.toUpperCase()}`, true); return }
    if (detecte && carte && detecte !== carte) {
      toast(`RECONNU COMME ${CONFIG[cible].nom.toUpperCase()} — DÉPOSÉ DANS SON BLOC`)
    }

    setTravail((p) => ({ ...p, [cible]: 'attente' }))
    setErreurs((p) => ({ ...p, [cible]: undefined }))

    enfiler(async () => {
      setTravail((p) => ({ ...p, [cible]: 'lecture' }))
      try {
        const lu = await lireFichier(fichier)
        const bilan = analyser(cible, lu, vue.current, fichier.name)
        if (bilan.confirmation && !(await demander(bilan))) {
          toast('DÉPÔT ABANDONNÉ — RIEN N’A ÉTÉ ÉCRIT')
          return
        }
        await appliquer(bilan)
        // Le brut part au coffre APRÈS l'écriture, et sans jamais la bloquer.
        // Personne ne conservait le fichier d'origine — c'est exactement ce qui
        // a manqué le jour de l'incident des tickets annulés, où il a fallu
        // deviner ce qu'ORIKA avait changé.
        void archiver(fichier, cible).catch(() => {})
      } catch (e) {
        // ecrireRapport a déjà dit pourquoi ; ici on le laisse à l'écran, dans le
        // bloc du fichier concerné, comme le faisait la V2.
        setErreurs((p) => ({ ...p, [cible]: messageErreur(e) }))
      } finally {
        setTravail((p) => ({ ...p, [cible]: undefined }))
      }
    })
  }

  /** Dépôt groupé : le type est détecté, l'ordre des fichiers n'importe pas. */
  const deposerEnVrac = async (liste: File[]) => {
    // `await` sur la seule reconnaissance — la file, elle, tient l'ordre des
    // écritures. Les cartes s'allument donc toutes les quatre dès le lâcher.
    for (const fichier of liste) await deposer(fichier, null)
  }

  return (
    <div
      // preventDefault inconditionnel, AVANT la garde : un dragover non annulé
      // retire l'élément des cibles de dépôt, et le navigateur ouvre alors le
      // CSV lâché à la place de l'application. En lecture seule on absorbe donc
      // l'événement — et on ne fait rien de plus.
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault()
        if (lectureSeule) return
        void deposerEnVrac(Array.from(e.dataTransfer.files))
      }}
    >
      {lectureSeule && (
        <Notice>
          <b>Étape validée</b> — les dépôts sont figés. Les états restent consultables ;
          pour ré-importer, rouvre l’étape.
        </Notice>
      )}

      {absents === 0
        ? <Notice c="ok">
            <b>Les quatre états sont déposés</b> — le parcours du jour est complet.
            {vides > 0 && ` ${pluriel(vides, 'fichier ne contient', 'fichiers ne contiennent')} aucune `
              + 'ligne : c’est un fait du jour, pas un manque — les étapes concernées seront grisées.'}
          </Notice>
        : <Notice c="warn">
            <b>{pluriel(absents, 'état manquant', 'états manquants')}</b> — les étapes qui en
            dépendent restent à faire tant que le fichier n’est pas là : une absence d’import n’est
            jamais une absence de travail.
          </Notice>}

      {regrisageDu && !lectureSeule && (
        <div style={{ marginTop: 10 }}>
          <Notice
            c="warn"
            action={
              <button
                className="btn pri sm"
                data-encours={regrisageEnCours ? 'true' : undefined}
                onClick={() => { void reevaluer(true) }}
              >
                Réévaluer le grisage
              </button>
            }
          >
            <b>Le grisage est en retard sur l’import</b> — les données sont écrites, mais les étapes
            qui en dépendent n’ont pas encore été (dé)grisées.
          </Notice>
        </div>
      )}

      {attente.length > 0 && (
        <Card
          titre={`Déposés par l’extension ORIKA — ${pluriel(attente.length, 'fichier en attente', 'fichiers en attente')}`}
          lampe="act"
          style={{ marginBottom: 12 }}
          actions={<Chip c="act">BRUT CONSERVÉ</Chip>}
        >
          {attente.map((l) => {
            const occupe = ingestion === l.id
            return (
              <div className="li" key={l.id}>
                <Lamp c="act" />
                <div className="body">
                  <div className="t">{CONFIG[l.type].nom}</div>
                  <div className="s">
                    {(l.fichier ?? '—').toUpperCase()}
                    {l.octets ? ` · ${Math.round(l.octets / 1024)} KO` : ''}
                    {' · DÉPOSÉ À '}{heure(l.depose_at)}
                    {l.source === 'manuel' ? ' · À LA MAIN' : ' · PAR L’EXTENSION'}
                  </div>
                </div>
                {!lectureSeule && (
                  <>
                    <button className="btn pri sm" data-encours={occupe ? 'true' : undefined}
                            onClick={() => { void ingerer(l) }}>
                      {occupe ? 'Ingestion…' : 'Ingérer'}
                    </button>
                    <button className="btn gh sm" disabled={occupe}
                            onClick={() => { void ecarter(l) }}>Écarter</button>
                  </>
                )}
              </div>
            )
          })}
          <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
            L’extension dépose le fichier brut ; c’est FlashCP qui le lit, le compare à ce qui est
            déjà là et l’écrit — même chemin, même bilan de ré-import, même confirmation avant
            écrasement qu’un dépôt à la main. Un fichier écarté reste archivé.
          </div>
        </Card>
      )}

      <div className="rs-bar" aria-hidden="true">
        {etats.map((e, i) => (
          <i key={fichiers[i]?.id ?? i} className={e === 'charge' ? 'done' : e === 'vide' ? 'skip' : ''} />
        ))}
      </div>

      <Kpis items={[
        { k: 'États déposés', v: `${4 - absents} / 4`, s: vides > 0 ? pluriel(vides, 'vide') : 'aucun vide' },
        { k: 'Lignes du jour', v: lignesJour, s: 'toutes familles' },
        // Sans dépôt dans cette session, rien n'a été mesuré : afficher « 0 » en
        // vert annoncerait une conformité qu'on n'a pas constatée.
        depotsSession === 0
          ? { k: 'Statuts détachés', v: '—', s: 'aucun dépôt cette session' }
          : {
              k: 'Statuts détachés', v: detaches, s: `sur ${pluriel(depotsSession, 'dépôt')}`,
              couleur: couleurEcartUnites(detaches),
            },
      ]} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
        {fichiers.map((f, i) => (
          <BlocFichier
            key={f.id}
            f={f}
            etat={etats[i] ?? 'absent'}
            depot={depotDe(f.id)}
            bilan={bilans[f.id] ?? null}
            alertes={alertes[f.id] ?? null}
            erreur={erreurs[f.id] ?? null}
            travail={travail[f.id] ?? null}
            lectureSeule={lectureSeule}
            deposer={(type, fichier) => { void deposer(fichier, type) }}
          />
        ))}
      </div>

      {!lectureSeule && (
        <div style={{ marginTop: 12 }}>
          <Notice
            // Le rappel discret s'efface quand le bandeau ambre porte déjà
            // l'action : deux boutons du même nom à l'écran, c'est une seconde
            // perdue à choisir lequel — et un doute sur le fait qu'ils diffèrent.
            action={regrisageDu ? undefined : (
              <button
                className="btn gh sm"
                data-encours={regrisageEnCours ? 'true' : undefined}
                onClick={() => { void reevaluer(true) }}
              >
                Réévaluer le grisage
              </button>
            )}
          >
            <b>Détection automatique</b> — glisse plusieurs fichiers d’un coup n’importe où sur
            l’écran : le type est reconnu au nom et à la première ligne, l’ordre n’a pas
            d’importance, et un fichier lâché sur la mauvaise carte rejoint la sienne. Un ré-import
            qui fait perdre des lignes ou des statuts demande confirmation ; les autres passent, et
            leur bilan reste affiché.
          </Notice>
        </div>
      )}

      <div style={{ marginTop: 12 }}>
        <Chantier titre="Archivage du CSV brut">
          Le fichier déposé n’est pas conservé : seul le résultat de son analyse est écrit. La V2
          ne le gardait pas non plus — mais elle ne le remplaçait pas sans prévenir. Garder le CSV
          tel quel dans <span className="mo">fc-pieces</span> permettrait de rejouer un import le
          jour où ORIKA change de format. Le bucket existe, la convention de chemin reste à fixer.
        </Chantier>
      </div>

      {aConfirmer && (
        <Modale
          large
          titre="Bilan de ré-import"
          sous={`${CONFIG[aConfirmer.type].nom} — ${aConfirmer.fichier} (${aConfirmer.encodage}). Chaque ligne porte un identifiant stable d’un export à l’autre : une ligne retrouvée garde le travail déjà posé dessus, une ligne qui a bougé le perd.`}
          fermer={() => trancher(false)}
        >
          <TableauBilan b={aConfirmer} />

          {aConfirmer.lignes === 0 && (
            <div style={{ marginTop: 10 }}>
              {aConfirmer.avant > 0
                ? <Notice c="stop">
                    <b>Ce fichier ne produit aucune ligne.</b> L’écrire remplacerait les{' '}
                    {pluriel(aConfirmer.avant, 'ligne')} du jour par du vide — vérifie que c’est le
                    bon export avant de continuer.
                  </Notice>
                : <Notice c="warn">
                    <b>Ce fichier ne produit aucune ligne.</b> Rien n’est écrasé — ce bloc était
                    vide. Mais déposer zéro ligne, c’est déclarer qu’il n’y en a pas eu aujourd’hui :
                    les étapes qui en dépendent seront grisées. Si ce n’est pas le bon export,
                    renonce.
                  </Notice>}
            </div>
          )}

          {aConfirmer.avertissements.map((a) => (
            <div key={a} style={{ marginTop: 10 }}>
              <Notice c="warn">{a}</Notice>
            </div>
          ))}

          <div className="btns">
            {/* Le refus est le défaut : c'est lui qui a le focus, donc Entrée
                renonce et Échap ferme. On ne valide une perte que d'un clic. */}
            <button className="btn" autoFocus onClick={() => trancher(false)}>
              Renoncer au dépôt
            </button>
            {/* Le ton dit ce qu'on s'apprête à faire : rouge quand on perd
                quelque chose, ambre quand on passe outre un doute. */}
            <button
              className={`btn ${aConfirmer.perte ? 'stop' : 'warn'} lg`}
              onClick={() => trancher(true)}
            >
              {libelleRemplacer(aConfirmer)}
            </button>
          </div>
        </Modale>
      )}
    </div>
  )
}

/** Le bouton dit ce qu'on perd, pas « OK ». Et quand on ne perd rien, il le dit aussi. */
function libelleRemplacer(b: Bilan): string {
  if (b.lignes === 0 && b.avant > 0) return 'Remplacer par du vide'
  if (b.orphelins > 0) return `Remplacer et détacher ${pluriel(b.orphelins, 'statut')}`
  if (b.retirees > 0) return `Remplacer — ${pluriel(b.retirees, 'ligne')} en moins`
  if (b.lignes === 0) return 'Déposer — aucune ligne ce jour'
  return 'Importer malgré l’encodage'
}

// ── Un bloc par fichier ────────────────────────────────────────────────────

function BlocFichier({ f, etat, depot, bilan, alertes, erreur, travail, lectureSeule, deposer }: {
  f: FichierImport
  etat: EtatBloc
  depot: Depot | null
  bilan: Bilan | null
  alertes: string[] | null
  erreur: string | null
  travail: Travail | null
  lectureSeule: boolean
  deposer: (type: TypeFichier, fichier: File) => void
}) {
  const cfg = CONFIG[f.id]
  const ref = useRef<HTMLInputElement>(null)
  const [survol, setSurvol] = useState(false)

  // Un dépôt qui a détaché du travail laisse la carte en ambre : la lampe verte
  // ne dit qu'une chose, « rien à signaler ».
  const lampe: Couleur = erreur ? 'stop' : bilan && bilan.orphelins > 0 ? 'warn' : LAMPE[etat]

  return (
    <Card
      titre={cfg.nom}
      lampe={lampe}
      // La pastille suit la lampe. Jugée sur le seul état du fichier, elle
      // restait VERTE — « CHARGÉ » — sur une carte dont la lampe venait de
      // passer à l'ambre parce que le dépôt avait détaché des statuts : deux
      // signaux contraires à trois centimètres l'un de l'autre, et c'est le vert
      // qu'on croit. Le fichier est bien chargé ; il n'est pas pour autant sans
      // rien à signaler, et c'est ce second point que la couleur porte.
      actions={<Chip c={lampe === 'ok' ? 'ok' : 'neutre'}>{MOT[etat]}</Chip>}
    >
      <div style={{ padding: '10px 12px' }}>
        <div className="mo hint">{cfg.motif}</div>
        <div style={{ fontSize: 12, color: 'var(--ink2)', marginTop: 4 }}>{cfg.dest}</div>
        <div className="mo" style={{ fontSize: 11, marginTop: 8, color: 'var(--ink3)' }}>
          {etat === 'vide' ? 'déposé, aucune ligne' : etat === 'absent' ? 'non déposé' : f.detail}
          {depot ? ` · déposé à ${heure(depot.at)}` : ''}
          {depot?.encodage ? ` · ${depot.encodage}` : ''}
        </div>
        {depot && <div className="hint" style={{ overflowWrap: 'anywhere' }}>{depot.fichier}</div>}
      </div>

      {bilan && (
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <TableauBilan b={bilan} />
        </div>
      )}

      {alertes?.map((a) => (
        <div key={a} style={{ padding: '0 12px 10px' }}>
          <Notice c="warn">{a}</Notice>
        </div>
      ))}

      {erreur && (
        <div style={{ padding: '0 12px 10px' }}>
          <Notice c="stop">{erreur}</Notice>
        </div>
      )}

      {!lectureSeule && (
        <div style={{ padding: '0 12px 12px' }}>
          <div
            className={`dz${survol ? ' over' : ''}`}
            role="button"
            tabIndex={0}
            aria-label={`Déposer ${cfg.nom}`}
            aria-busy={travail !== null}
            onDragOver={(e) => { e.preventDefault(); setSurvol(true) }}
            onDragLeave={() => setSurvol(false)}
            onDrop={(e) => {
              // stopPropagation : sans elle l'événement remonte au dépôt global
              // et le même fichier serait analysé deux fois.
              e.preventDefault(); e.stopPropagation(); setSurvol(false)
              const fichier = e.dataTransfer.files[0]
              if (fichier) deposer(f.id, fichier)
            }}
            onClick={() => ref.current?.click()}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ref.current?.click() } }}
          >
            {travail === 'lecture' ? 'Lecture du fichier…'
              : travail === 'attente' ? 'Accepté — en attente du dépôt précédent…'
              : etat === 'absent' ? 'Glisser ou cliquer pour déposer'
              : 'Remplacer — glisser ou cliquer'}
            <input
              ref={ref} type="file" accept=".csv" style={{ display: 'none' }}
              // Le clic programmatique remonterait à la zone, qui le relancerait.
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => {
                const fichier = e.target.files?.[0]
                // On vide le champ : sans ça, redéposer le MÊME fichier corrigé
                // n'émet aucun événement.
                e.target.value = ''
                if (fichier) deposer(f.id, fichier)
              }}
            />
          </div>
        </div>
      )}
    </Card>
  )
}

function TableauBilan({ b }: { b: Bilan }) {
  const net = b.familles.reduce((s, f) => s + f.apres - f.avant, 0)
  return (
    <>
      <div className="scrollx">
        <table className="t">
          <thead>
            <tr>
              <th>Famille</th>
              <th className="n">Avant</th>
              <th className="n">Après</th>
              <th className="n">Ajoutées</th>
              <th className="n">Retirées</th>
              {/* « Inchangées » était faux pour deux familles sur huit : un
                  opérateur est identifié par son badge (`op_1234`) et un
                  monnayeur par son numéro — leur ligne garde son identifiant
                  quand ses montants changent. Le mot juste est RETROUVÉES, et
                  c'est de toute façon la seule notion qui compte ici : un
                  identifiant retrouvé, c'est le travail posé dessus qui survit. */}
              <th className="n" title="Lignes retrouvées à l’identifiant près — c’est ce qui fait survivre le travail déjà posé dessus.">
                Retrouvées
              </th>
            </tr>
          </thead>
          <tbody>
            {b.familles.map((f) => {
              // Un premier import n'est pas un écart : il n'y a rien à quoi
              // diverger. Dès qu'il y avait quelque chose avant, tout mouvement
              // — dans les deux sens — est une divergence entre deux exports du
              // même jour, et une divergence ne se félicite jamais en vert.
              const reimport = f.avant > 0
              return (
                <tr key={f.nom}>
                  <td>{f.nom}</td>
                  <td className="n" style={{ color: 'var(--ink3)' }}>{f.avant}</td>
                  <td className="n">{f.apres}</td>
                  <td className="n" style={{
                    color: f.ajoutees === 0 ? 'var(--ink4)'
                      : reimport ? couleurEcartUnites(f.ajoutees) : 'var(--ink2)',
                  }}>
                    {f.ajoutees ? `+${f.ajoutees}` : '—'}
                  </td>
                  <td className="n" style={{
                    color: f.retirees === 0 ? 'var(--ink4)' : couleurEcartUnites(f.retirees),
                  }}>
                    {f.retirees ? `−${f.retirees}` : '—'}
                  </td>
                  <td className="n" style={{ color: 'var(--ink3)' }}>{f.communs}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div className="hint" style={{ padding: '8px 12px' }}>
        {net !== 0 && (
          <>
            <span className="mo" style={{ color: couleurEcartUnites(net) }}>
              {fmtEcartUnites(net, 'ligne')}
            </span>{' '}au total ·{' '}
          </>
        )}
        {b.conserves} {CONFIG[b.type].statuts} conservés
        {b.orphelins > 0
          ? <> · <span className="mo" style={{ color: couleurEcartUnites(b.orphelins) }}>
              {pluriel(b.orphelins, 'détaché')}
            </span> — leur ligne n’est plus dans le fichier, le travail déjà fait dessus ne compte plus.</>
          : ' — rien ne se détache.'}
      </div>
    </>
  )
}
